// "Résumé" tab for a single delivery form. Mirrors the OT traceability
// summary's visual language (KPI tiles + verdict cards) but trades the
// shipment context for delivery-form metadata: declared weight + bag
// count + lock state. Numbers come straight off the delivery digest
// the backend computes for this id.

import { useTranslation } from "react-i18next"
import { MapPin, Receipt, Users } from "lucide-react"

import { Card } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { formatDate, formatNumber, kgToMt } from "@/lib/format"
import type { DeliveryFormTraceability } from "@/features/delivery-forms/api/types"

interface Props {
  data: DeliveryFormTraceability
}

export function DeliverySummaryTab({ data }: Props) {
  const { t } = useTranslation(["intake", "transit"])
  const { deliveryForm: df, traceability } = data
  const { summary } = traceability
  // A single-delivery view: the per-delivery digest is the source of
  // truth for the headline metrics (traceability %, hasRisks). The
  // OT-level summary roll-up still surfaces the producer/parcel/
  // transaction totals.
  const digest = traceability.deliveries[0]
  const traceabilityPct = digest?.traceabilityStatus ?? 0
  const traceableProducers = summary.producers.traceable
  const totalProducers = summary.producers.total
  const hasRisks = digest?.hasRisks ?? false

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile
          label={t("deliveryForm.summary.kpi.weight", { ns: "intake" })}
          primary={
            df.netKilogramsAttached != null
              ? `${formatNumber(kgToMt(df.netKilogramsAttached), 2)} MT`
              : "—"
          }
          secondary={t("deliveryForm.summary.kpi.weightSub", {
            ns: "intake",
            kg:
              df.netKilogramsAttached != null
                ? formatNumber(df.netKilogramsAttached, 0)
                : "—",
          })}
        />
        <KpiTile
          label={t("deliveryForm.summary.kpi.traceability", { ns: "intake" })}
          primary={`${traceabilityPct}%`}
          secondary={t("deliveryForm.summary.kpi.traceabilitySub", {
            ns: "intake",
            traceable: traceableProducers,
            total: totalProducers,
          })}
          tone={traceabilityPct === 100 ? "good" : "warn"}
        />
        <KpiTile
          label={t("deliveryForm.summary.kpi.eudr", { ns: "intake" })}
          primary={
            digest?.isCompliant
              ? t("terminer.eudrStatus.PASS", { ns: "transit" })
              : hasRisks
                ? t("terminer.eudrStatus.FAIL", { ns: "transit" })
                : t("terminer.eudrStatus.IN_PROGRESS", { ns: "transit" })
          }
          tone={digest?.isCompliant ? "good" : hasRisks ? "bad" : "warn"}
        />
      </div>

      <Card className="flex flex-col gap-0 p-0">
        <div className="px-5 py-3">
          <h2 className="text-sm font-semibold tracking-tight text-foreground">
            {t("deliveryForm.summary.data", { ns: "intake" })}
          </h2>
        </div>
        <Separator />
        <div className="grid grid-cols-1 divide-y sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <DataCell
            icon={<Users className="size-4 text-muted-foreground" />}
            label={t("deliveryForm.summary.producers", { ns: "intake" })}
            value={summary.producers.total.toLocaleString()}
          />
          <DataCell
            icon={<MapPin className="size-4 text-muted-foreground" />}
            label={t("deliveryForm.summary.parcels", { ns: "intake" })}
            value={summary.parcels.total.toLocaleString()}
          />
          <DataCell
            icon={<Receipt className="size-4 text-muted-foreground" />}
            label={t("deliveryForm.summary.transactions", { ns: "intake" })}
            value={summary.purchases.total.toLocaleString()}
          />
        </div>
      </Card>

      <Card className="flex flex-col gap-3 p-5">
        <h2 className="text-sm font-semibold tracking-tight text-foreground">
          {t("deliveryForm.summary.title", { ns: "intake" })}
        </h2>
        <Separator />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Fact
            label={t("deliveryForm.header.cooperative", { ns: "intake" })}
            value={df.cooperativeName ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.order", { ns: "intake" })}
            value={df.orderNumber ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.contract", { ns: "intake" })}
            value={df.contractNumber ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.client", { ns: "intake" })}
            value={df.clientName ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.slip", { ns: "intake" })}
            value={df.billOfLadingNumber ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.shippingForm", { ns: "intake" })}
            value={df.shippingFormNumber ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.truck", { ns: "intake" })}
            value={df.truckRegistrationNumber ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.carrier", { ns: "intake" })}
            value={df.carrierName ?? "—"}
          />
          <Fact
            label={t("deliveryForm.header.shippingDate", { ns: "intake" })}
            value={formatDate(df.shippingDate)}
          />
          <Fact
            label={t("deliveryForm.header.exportDate", { ns: "intake" })}
            value={formatDate(df.dateOfExport)}
          />
          <Fact
            label={t("deliveryForm.header.declaredWeight", { ns: "intake" })}
            value={
              df.netKilogramsAttached != null
                ? `${formatNumber(kgToMt(df.netKilogramsAttached), 2)} MT`
                : "—"
            }
          />
          <Fact
            label={t("deliveryForm.header.bags", { ns: "intake" })}
            value={
              df.numberOfBagsAttached != null
                ? df.numberOfBagsAttached.toLocaleString()
                : "—"
            }
          />
        </div>
      </Card>
    </div>
  )
}

type Tone = "good" | "warn" | "bad" | "neutral"

function KpiTile({
  label,
  primary,
  secondary,
  tone = "neutral",
}: {
  label: string
  primary: string
  secondary?: string
  tone?: Tone
}) {
  const palette: Record<Tone, string> = {
    good: "border-emerald-200 bg-emerald-50/60 dark:border-emerald-500/30 dark:bg-emerald-500/10",
    warn: "border-amber-200 bg-amber-50/60 dark:border-amber-500/30 dark:bg-amber-500/10",
    bad: "border-red-200 bg-red-50/60 dark:border-red-500/30 dark:bg-red-500/10",
    neutral: "border-border bg-card",
  }
  return (
    <Card className={`flex flex-col gap-1 p-5 ${palette[tone]}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-foreground">
        {primary}
      </div>
      {secondary ? (
        <div className="text-xs text-muted-foreground">{secondary}</div>
      ) : null}
    </Card>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col rounded-md border bg-muted/30 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm font-medium text-foreground">
        {value || "—"}
      </div>
    </div>
  )
}

function DataCell({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-lg font-semibold tabular-nums text-foreground">
        {value}
      </div>
    </div>
  )
}
